// @ts-strict-ignore
import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { cloneDeep } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Callout, Checkbox, Dialog, toast } from '@node-space/ui-components'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useAccountApprovalsPreferenceMutation } from 'hooks/mutations/useAccountApprovalsPreferenceMutation'
import { useApprovalsContext } from 'hooks/useApprovalsContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ApprovalRulesData, UpdateAccountApprovalsPreferenceV2 } from 'types/approvals'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { logSentryError } from 'utils/sentry'
import track from 'utils/tracker'
import ApprovalRangeForm from './ApprovalRangeForm'
import {
  useApiInitiatedApproval,
  useApprovalFlowsCheckboxOptions,
} from './hooks/useApprovalsFlowSettings'

const DEFAULT_APPROVAL_LIMIT: ApprovalRulesData[] = [
  {
    minAmount: 0.0,
    maxAmount: Approval.RULES.INFINITY,
    approvalsCount: 1,
  },
]

const ApprovalPreferenceSettingsModalV2 = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { currentAccount } = useAccountsContext()
  const queryClient = useQueryClient()
  const {
    permissions,
    approvalsConfig: {
      approvalPreferences,
      approvalPreferencesLimits,
      handleApprovalPreferenceSettingsModal,
      handleApprovalPreferenceSuccessModal,
    },
  } = useApprovalsContext()
  const currentAccountReference = currentAccount?.reference || ''

  const { mutate: updateAccountApprovalsPreference, isPending: updatePreferenceIsProcessing } =
    useAccountApprovalsPreferenceMutation()

  const { approvalFlowCheckboxOptions, approvalFlowTranslations } =
    useApprovalFlowsCheckboxOptions(approvalPreferences)
  const approvalRangeLimits = approvalPreferencesLimits?.length
    ? approvalPreferencesLimits
    : DEFAULT_APPROVAL_LIMIT

  const [checkBoxItems, setCheckboxItems] = useState(approvalFlowCheckboxOptions)
  const [isRefetching, setIsRefetching] = useState(false)
  const [preferencesConfig, setPreferencesConfig] = useState([])
  const [approvalRulesData, setApprovalRulesData] =
    useState<ApprovalRulesData[]>(approvalRangeLimits)
  const [isApprovalRuleInvalid, setIsApprovalRuleInvalid] = useState(false)

  const { isApiApprovalFlowPresent, isApiApprovalEnabled, apiApprovalLabel } =
    useApiInitiatedApproval(approvalPreferences)
  const [isApiApprovalChecked, setIsApiApprovalChecked] = useState(isApiApprovalEnabled)

  const onCheckboxChange = (index: number) => {
    const copyOfCheckboxItems = cloneDeep(checkBoxItems)
    copyOfCheckboxItems[index].checked = !copyOfCheckboxItems?.[index]?.checked

    const existingValue = approvalFlowCheckboxOptions?.[index]?.checked
    if (existingValue !== copyOfCheckboxItems[index].checked) {
      preferencesConfig[index] = {
        flow: checkBoxItems?.[index]?.value,
        enabled: copyOfCheckboxItems?.[index]?.checked,
      }
      setPreferencesConfig(preferencesConfig)
    } else {
      delete preferencesConfig[index]
      setPreferencesConfig(preferencesConfig)
    }
    setCheckboxItems(copyOfCheckboxItems)
  }

  const updatedPreferencesConfig = preferencesConfig?.filter(Boolean)

  const getApprovalsPreferencePayload = (): UpdateAccountApprovalsPreferenceV2 => {
    const preferences = checkBoxItems?.map(checkBoxItem => {
      return {
        flow: checkBoxItem?.value,
        enabled: checkBoxItem?.checked,
      }
    })
    if (isApiApprovalFlowPresent) {
      preferences.push({
        flow: Approval.FLOWS.CRYPTO_PAYOUT_API,
        enabled: isApiApprovalChecked,
      })
    }
    const limits = approvalRulesData
    const limitsLength = limits?.length
    if (limitsLength) {
      const lastLimit = limits?.[limitsLength - 1]
      if (lastLimit.maxAmount === Approval.RULES.INFINITY) {
        lastLimit.maxAmount = null
      }
    }
    if (limitsLength === 1) {
      limits[0].approvalsCount = limits?.[0]?.approvalsCount || 1
    }

    return {
      parentId: currentAccountReference,
      preference: Approval.ACCOUNT,
      preferences,
      limits,
    }
  }

  const handleApprovalPreferenceUpdate = () => {
    const accountApprovalsPreference = getApprovalsPreferencePayload()

    setIsRefetching(true)
    updateAccountApprovalsPreference(
      { data: accountApprovalsPreference },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: reactQueryKeys.accountApprovalsPreference(currentAccountReference),
          })
          setIsRefetching(false)
          handleApprovalPreferenceSuccessModal({ isVisible: true })
          track.Amp.track(`${AmplitudeEvent.UPDATE_APPROVALS_PREFERENCE} V3`, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.CLICK,
            updatedFlows: updatedPreferencesConfig,
          })
        },
        onError: (error: BaseErrorResponse) => {
          toast({ header: t('oopsSomethingWentWrongContracted'), variant: 'error' })
          logSentryError(
            'Error from ApprovalPreferenceSettingsModalV2 - handleApprovalPreferenceUpdate',
            error
          )
          setIsRefetching(false)
        },
      }
    )
  }

  const handleClose = () => handleApprovalPreferenceSettingsModal({ isVisible: false })

  return (
    <Dialog
      title={
        permissions?.canEdit ? t('approvals.manageApprovalRules') : t('approvals.viewApprovalRules')
      }
      open
      onClose={handleClose}
      type="utility"
      ActionButtons={
        permissions?.canEdit
          ? [
              <Button
                key="save"
                loading={updatePreferenceIsProcessing || isRefetching}
                disabled={isApprovalRuleInvalid || updatePreferenceIsProcessing || isRefetching}
                onClick={handleApprovalPreferenceUpdate}
              >
                {t('save')}
              </Button>,
              <Button key="cancel" variant="secondary" onClick={handleClose}>
                {t('cancel')}
              </Button>,
            ]
          : [
              <Button key="done" onClick={handleClose}>
                {t('done')}
              </Button>,
            ]
      }
    >
      <>
        <Callout
          variant="info"
          GhostButton={
            <Button
              onClick={() => navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_USER_PROFILE}`)}
            >
              {t('approvals.changeYourBaseCurrency')}
            </Button>
          }
        >
          {t('approvals.manageApprovalRulesDescription')}
        </Callout>
        <Box>
          <p className="text-body-regular text-foreground-prominent my-3">
            {t('approvals.typeOfOutgoingPayment')}
          </p>
          {checkBoxItems?.length !== Object.keys(approvalFlowTranslations)?.length ? (
            <Callout variant="error">{t('approvals.approvalFlowConfigurationIssue')}</Callout>
          ) : (
            checkBoxItems?.map((item, index) => (
              <Box paddingB={8} key={item?.value}>
                <Checkbox
                  id={`approval-checkbox-${item?.value}`}
                  name={`approval-checkbox-${item?.value}`}
                  testId={`approval-checkbox-${item?.value}`}
                  disabled={!permissions?.canEdit}
                  label={item?.label}
                  checked={item?.checked}
                  onCheckedChange={() => onCheckboxChange(index)}
                />
              </Box>
            ))
          )}
        </Box>
        {isApiApprovalFlowPresent && (
          <Box>
            <p className="text-body-regular text-foreground-prominent my-3">
              {t('approvals.apiInitiatedPayments')}
            </p>
            <Checkbox
              id="approval-checkbox-api"
              name="approval-checkbox-api"
              testId="approval-checkbox-api"
              disabled={!permissions?.canEdit}
              label={apiApprovalLabel}
              checked={isApiApprovalChecked}
              onCheckedChange={() => setIsApiApprovalChecked(prev => !prev)}
            />
          </Box>
        )}
        <ApprovalRangeForm
          approvalRulesData={approvalRulesData}
          setApprovalRulesData={setApprovalRulesData}
          isApprovalRuleInvalid={isApprovalRuleInvalid}
          setIsApprovalRuleInvalid={setIsApprovalRuleInvalid}
        />
      </>
    </Dialog>
  )
}

export default React.memo(ApprovalPreferenceSettingsModalV2)
